import { Button, Dropdown, Image, Input, Modal, Popconfirm, Spin, Tag, Tooltip, Upload, message } from 'antd';
import {
    CheckOutlined, CodeOutlined, CopyOutlined, DeleteOutlined, FileImageOutlined, FilePdfOutlined, FormOutlined,
    PaperClipOutlined, SendOutlined, VideoCameraOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { serverFetch, fileUrl } from '@server';
import { showError, fmtDateTime } from '@extensions/utils';
import { MessageBody } from '@extensions/html';
import { CHANNEL_BEELINE, CHANNEL_MAX_WAPPI, CHANNEL_TG_WAPPI } from '@extensions/dicts';
import ResizableDrawer from '@controls/resizable-drawer/resizable-drawer';
import PdfModal, { isPdfFile } from '@controls/pdf-viewer/pdf-modal';
import CallCard from './call-card';

const fmtSize = (size) => (size >= 1024 * 1024
    ? `${(size / 1024 / 1024).toFixed(1)} МБ`
    : `${Math.max(1, Math.round(size / 1024))} КБ`);

const TRANSCRIPT_DONE = 3;
const TRANSCRIPT_RUNNING = 4;
const TRANSCRIPT_ERROR = -1;

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });
const AUDIO_EXT = ['.mp3', '.ogg', '.opus', '.oga', '.wav', '.m4a', '.aac'];

const isAudioFile = (file) => {
    if ((file.contentType ?? '').startsWith('audio/')) return true;
    const name = (file.name ?? '').toLowerCase();
    return AUDIO_EXT.some((ext) => name.endsWith(ext));
};

const VIDEO_EXT = ['.mp4', '.m4v', '.mov', '.webm'];
const isVideoFile = (file) => {
    if ((file.contentType ?? '').startsWith('video/')) return true;
    const name = (file.name ?? '').toLowerCase();
    return VIDEO_EXT.some((ext) => name.endsWith(ext));
};

// Плеер голосового и кнопка расшифровки, если текста ещё нет
const AudioAttachment = ({ dialogId, file, messageBody, onTranscribed, canDelete, onDelete }) => {
    const [starting, setStarting] = useState(false);
    const watchId = useRef(0);
    const transcript = (file.transcript ?? '').trim();
    const status = file.transcriptStatus ?? 0;
    useEffect(() => { setStarting(false); }, [status, transcript]);
    useEffect(() => () => { watchId.current += 1; }, []);
    const transcribing = starting || status === TRANSCRIPT_RUNNING;
    const sameAsBody = transcript.length > 0 && transcript === (messageBody ?? '').trim();

    const reportFile = (fresh) => {
        if (!fresh) {
            message.warning('Не удалось получить результат расшифровки');
            return;
        }
        if (fresh.transcriptStatus === TRANSCRIPT_ERROR) message.error(fresh.transcriptNote || 'Не удалось расшифровать');
        else if (fresh.transcriptStatus === TRANSCRIPT_DONE || (fresh.transcript ?? '').trim()) message.success('Запись расшифрована');
        else message.warning(fresh.transcriptNote || 'Расшифровка не выполнена');
    };

    const watchFile = async (gen) => {
        let delay = 1000;
        const deadline = Date.now() + 10 * 60 * 1000;
        while (watchId.current === gen && Date.now() < deadline) {
            await sleep(delay);
            delay = 2000;
            if (watchId.current !== gen) return;
            let list;
            try {
                list = await serverFetch(`dialog/${dialogId}/messages`);
            } catch (e) {
                if (watchId.current !== gen) return;
                showError(e);
                setStarting(false);
                return;
            }
            const msg = (list ?? []).find((item) => (item.files ?? []).some((f) => f.id === file.id));
            const fresh = msg?.files?.find((f) => f.id === file.id);
            if (fresh && (fresh.transcriptStatus ?? 0) === TRANSCRIPT_RUNNING) continue;
            if (watchId.current !== gen) return;
            reportFile(fresh);
            if (msg) onTranscribed?.(msg);
            setStarting(false);
            return;
        }
        if (watchId.current === gen) {
            message.warning('Расшифровка не завершилась');
            setStarting(false);
        }
    };

    const onStart = () => {
        if (starting) return;
        setStarting(true);
        const gen = watchId.current + 1;
        watchId.current = gen;
        serverFetch(`file/${file.id}/transcribe`, { method: 'POST' })
            .then((msg) => {
                onTranscribed?.(msg);
                return watchFile(gen);
            })
            .catch((e) => {
                if (watchId.current === gen) watchId.current += 1;
                showError(e);
                setStarting(false);
            });
    };
    return (
        <div>
            <div className="dlg-audio-row">
                <audio className="call-audio" controls src={fileUrl(file.id, true)} />
                {canDelete && (
                    <Popconfirm
                        title="Удалить аудио?"
                        okText="Удалить"
                        cancelText="Отмена"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => onDelete(file)}
                    >
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="Удалить аудио" />
                    </Popconfirm>
                )}
            </div>
            {transcript && !sameAsBody && <div className="dlg-audio-text">{transcript}</div>}
            {!transcript && (
                <Button size="small" type="link" loading={transcribing} disabled={transcribing} onClick={onStart}>
                    {transcribing ? 'Расшифровка…' : 'Расшифровать'}
                </Button>
            )}
            {status === TRANSCRIPT_ERROR && file.transcriptNote
                ? <div className="dlg-msg-error">{file.transcriptNote}</div>
                : null}
        </div>
    );
};

// MessageDirection: 1 — входящее, -1 — исходящее; MessageStatus: -1 Error, 5 Sending, 1 Sent
const MSG_INCOMING = 1;
const MSG_STATUS_ERROR = -1;
const MSG_STATUS_SENDING = 5;

// Лента сообщений диалога в стиле task-comments: входящие слева, исходящие справа
const DialogMessages = ({ dialog, messages, setMessages, onReadChanged, onOpenContragent }) => {
    const user = useSelector((s) => s.user);
    const canDelete = !!user?.isSuperadmin;
    const [rawModal, setRawModal] = useState({ open: false, text: '' });
    const [videoModal, setVideoModal] = useState(null);
    const [pdfModal, setPdfModal] = useState(null);
    const [text, setText] = useState('');
    const [files, setFiles] = useState([]); // загруженные файлы-черновики [{id,name,size,...}]
    const [patterns, setPatterns] = useState([]);
    const [sending, setSending] = useState(false);
    const [calls, setCalls] = useState([]);
    const bottomRef = useRef(null);
    const isBeeline = (dialog?.channelVid ?? dialog?.vid) === CHANNEL_BEELINE;
    const readSent = useRef(false);

    const scrollToBottom = useCallback(() => {
        bottomRef.current?.scrollIntoView({ block: 'end' });
    }, []);

    useEffect(() => { scrollToBottom(); }, [messages.length, scrollToBottom]);

    const callStamp = messages.map((m) => `${m.id}:${(m.body ?? '').length}:${(m.files ?? []).length}`).join('|');
    const loadCalls = useCallback(() => {
        if (!isBeeline || !dialog?.id) return;
        serverFetch(`call?dialogId=${dialog.id}`).then(setCalls).catch(() => {});
    }, [isBeeline, dialog?.id]);

    const reloadMessages = useCallback(() => {
        if (!dialog?.id) return;
        serverFetch(`dialog/${dialog.id}/messages`).then(setMessages).catch(() => {});
    }, [dialog?.id, setMessages]);

    const onCallChanged = (fresh) => {
        if (fresh?.id) {
            setCalls((list) => list.map((item) => (item.id === fresh.id ? { ...item, ...fresh } : item)));
        } else {
            loadCalls();
        }
        reloadMessages();
    };

    useEffect(() => { loadCalls(); }, [loadCalls, callStamp]);

    // При открытии — отметить прочитанным
    useEffect(() => {
        if (readSent.current || !dialog?.id) return;
        readSent.current = true;
        serverFetch(`dialog/${dialog.id}/read`, { method: 'PUT' })
            .then(() => onReadChanged?.())
            .catch(() => {});
    }, [dialog?.id, onReadChanged]);

    // Шаблоны ответов — лениво при первом открытии меню
    const loadPatterns = useCallback(() => {
        serverFetch('messagepattern')
            .then((list) => setPatterns((list ?? []).filter((p) => p.active)))
            .catch(showError);
    }, []);

    const applyPattern = (id) => {
        serverFetch(`messagepattern/${id}/render?dialogId=${dialog.id}`)
            .then((r) => setText((t) => (t ? `${t}\n${r.body}` : r.body)))
            .catch(showError);
    };

    const onUpload = (file) => {
        const fd = new FormData();
        fd.append('files', file);
        serverFetch(`file?ownerVid=3&ownerId=${dialog.id}`, { method: 'POST', formData: fd })
            .then((saved) => setFiles((l) => [...l, ...(saved ?? [])]))
            .catch(showError);
        return false;
    };

    const onRemoveFile = (f) => {
        setFiles((l) => l.filter((x) => x.id !== f.id));
        serverFetch(`file/${f.id}`, { method: 'DELETE' }).catch(() => {});
    };

    const onSend = () => {
        const body = text.trim();
        if ((!body && !files.length) || sending) return;
        setSending(true);
        serverFetch(`dialog/${dialog.id}/messages`, {
            method: 'POST',
            bodyData: { body, fileIds: files.map((f) => f.id) },
        })
            .then((msg) => {
                if (msg?.id) {
                    setMessages((l) => (l.some((m) => m.id === msg.id) ? l : [...l, msg]));
                }
                setText('');
                setFiles([]);
            })
            .catch((e) => {
                showError(e);
                setText('');
                setFiles([]);
            })
            .finally(() => setSending(false));
    };

    const onMarkUnread = () => {
        serverFetch(`dialog/${dialog.id}/unread`, { method: 'PUT' })
            .then(() => onReadChanged?.())
            .catch(showError);
    };

    const clearFlag = (action) => {
        serverFetch(`dialog/${dialog.id}/${action}`, { method: 'PUT' })
            .then(() => onReadChanged?.())
            .catch(showError);
    };

    const onShowRaw = (messageId) => {
        serverFetch(`dialog/${dialog.id}/messages/${messageId}/raw`)
            .then((r) => {
                const raw = r?.raw ?? '';
                let text = raw;
                try {
                    text = JSON.stringify(JSON.parse(raw), null, 2);
                } catch (e) { /* показываем как есть */ }
                setRawModal({ open: true, text });
            })
            .catch(showError);
    };

    const onDeleteMessage = (messageId) => {
        serverFetch(`dialog/${dialog.id}/messages/${messageId}`, { method: 'DELETE' })
            .then(() => {
                setMessages((list) => list.filter((m) => m.id !== messageId));
                onReadChanged?.();
            })
            .catch(showError);
    };

    const onDeleteAudio = (file) => {
        serverFetch(`dialog/${dialog.id}/files/${file.id}`, { method: 'DELETE' })
            .then((msg) => {
                if (msg?.id) {
                    setMessages((list) => list.map((item) => (item.id === msg.id ? { ...item, ...msg } : item)));
                }
                onReadChanged?.();
            })
            .catch(showError);
    };

    // Индекс первого непрочитанного входящего — для разделителя
    const firstUnreadIdx = (() => {
        const lastRead = dialog?.lastMessageIdIsRead;
        if (lastRead == null || lastRead >= (dialog?.lastMessageId ?? 0)) return -1;
        return messages.findIndex((m) => m.direction === MSG_INCOMING && m.id > lastRead);
    })();

    const callFileIds = new Set(calls.map((c) => c.fileId).filter((id) => id));

    const renderAttachments = (m) => {
        const list = m.files ?? [];
        if (!list.length) return null;
        const images = list.filter((f) => f.isImage);
        const audios = list.filter((f) => !f.isImage && isAudioFile(f));
        const videos = list.filter((f) => !f.isImage && !isAudioFile(f) && isVideoFile(f));
        const pdfs = list.filter((f) => !f.isImage && !isAudioFile(f) && !isVideoFile(f) && isPdfFile(f));
        const others = list.filter((f) => !f.isImage && !isAudioFile(f) && !isVideoFile(f) && !isPdfFile(f));
        return (
            <div className="task-attachments">
                {images.length > 0 && (
                    <Image.PreviewGroup>
                        {images.map((f) => (
                            <Image
                                key={f.id}
                                width={112}
                                height={84}
                                style={{ objectFit: 'cover', borderRadius: 8 }}
                                src={fileUrl(f.id, true)}
                                alt={f.name}
                            />
                        ))}
                    </Image.PreviewGroup>
                )}
                {audios.map((f) => (callFileIds.has(f.id) ? (
                    <div key={f.id} className="dlg-audio-row">
                        <audio className="call-audio" controls src={fileUrl(f.id, true)} />
                        {canDelete && (
                            <Popconfirm
                                title="Удалить аудио?"
                                okText="Удалить"
                                cancelText="Отмена"
                                okButtonProps={{ danger: true }}
                                onConfirm={() => onDeleteAudio(f)}
                            >
                                <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="Удалить аудио" />
                            </Popconfirm>
                        )}
                    </div>
                ) : (
                    <AudioAttachment
                        key={f.id}
                        dialogId={dialog?.id}
                        file={f}
                        messageBody={m.body}
                        canDelete={canDelete}
                        onDelete={onDeleteAudio}
                        onTranscribed={(msg) => {
                            if (!msg?.id) return;
                            setMessages((list) => list.map((item) => (item.id === msg.id ? { ...item, ...msg } : item)));
                        }}
                    />
                )))}
                {videos.map((f) => (
                    <span key={f.id} className="task-attachment-file">
                        <a
                            href={fileUrl(f.id)}
                            onClick={(e) => { e.preventDefault(); setVideoModal(f); }}
                        >
                            <VideoCameraOutlined /> {f.name}
                        </a>
                        <span className="task-attachment-size">{fmtSize(f.size)}</span>
                    </span>
                ))}
                {pdfs.map((f) => (
                    <span key={f.id} className="task-attachment-file">
                        <a
                            href={fileUrl(f.id)}
                            onClick={(e) => { e.preventDefault(); setPdfModal(f); }}
                        >
                            <FilePdfOutlined /> {f.name}
                        </a>
                        <span className="task-attachment-size">{fmtSize(f.size)}</span>
                    </span>
                ))}
                {others.map((f) => (
                    <span key={f.id} className="task-attachment-file">
                        <a href={fileUrl(f.id)} target="_blank" rel="noreferrer">
                            <PaperClipOutlined /> {f.name}
                        </a>
                        <span className="task-attachment-size">{fmtSize(f.size)}</span>
                    </span>
                ))}
            </div>
        );
    };

    const renderStatus = (m) => {
        if (m.direction === MSG_INCOMING) return null;
        if (m.status === MSG_STATUS_SENDING) return <Spin size="small" />;
        if (m.status === MSG_STATUS_ERROR) {
            return <Tooltip title={m.errors}><span className="dlg-msg-error">Ошибка: {m.errors || 'не отправлено'}</span></Tooltip>;
        }
        return <CheckOutlined className="dlg-msg-sent" />;
    };

    const patternItems = patterns.map((p) => ({ key: String(p.id), label: p.name }));

    return (
        <div className="dlg-messages">
            {(dialog?.missed || dialog?.incoming) && !dialog?.deleted && (
                <div className="dlg-flags">
                    {dialog.missed && (
                        <Button size="small" onClick={() => clearFlag('clear-missed')}>Снять пропущенный</Button>
                    )}
                    {dialog.incoming && (
                        <Button size="small" onClick={() => clearFlag('clear-incoming')}>Снять входящее</Button>
                    )}
                </div>
            )}
            <div className="task-chat dlg-chat">
                {messages.length === 0 && <div className="task-chat-empty">Сообщений пока нет</div>}
                {messages.map((m, i) => {
                    const out = m.direction !== MSG_INCOMING;
                    return (
                        <div key={m.id}>
                            {i === firstUnreadIdx && (
                                <div className="dlg-unread-divider">Непрочитанные</div>
                            )}
                            <div className={`task-msg${out ? ' own' : ''}`}>
                                <div className="task-msg-bubble">
                                    <div className="task-msg-head">
                                        <b>{out
                                            ? (m.userName || 'Оператор')
                                            : (dialog?.isGroup
                                                ? (m.userName || 'Участник')
                                                : (dialog?.name || dialog?.enteredName || 'Клиент'))}</b>
                                        {m.contragentId ? (
                                            onOpenContragent ? (
                                                <Button
                                                    type="link" size="small"
                                                    style={{ padding: 0, height: 'auto', fontSize: 12 }}
                                                    onClick={() => onOpenContragent(m.contragentId)}
                                                >
                                                    {m.contragentName || `#${m.contragentId}`}
                                                </Button>
                                            ) : <span className="card-list-muted">{m.contragentName || `#${m.contragentId}`}</span>
                                        ) : null}
                                        <span>{fmtDateTime(m.createdOn)}</span>
                                        {renderStatus(m)}
                                        {canDelete && m.hasRaw && (
                                            <Button
                                                size="small"
                                                type="text"
                                                icon={<CodeOutlined />}
                                                aria-label="Сырой JSON"
                                                onClick={() => onShowRaw(m.id)}
                                            />
                                        )}
                                        {canDelete && (
                                            <Popconfirm
                                                title="Удалить сообщение?"
                                                okText="Удалить"
                                                cancelText="Отмена"
                                                okButtonProps={{ danger: true }}
                                                onConfirm={() => onDeleteMessage(m.id)}
                                            >
                                                <Button
                                                    className="dlg-msg-del"
                                                    size="small"
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    aria-label="Удалить сообщение"
                                                />
                                            </Popconfirm>
                                        )}
                                    </div>
                                    {m.body && <MessageBody className="task-msg-body" text={m.body} />}
                                    {m.url && (
                                        <div className="dlg-msg-page">
                                            <a href={m.url} target="_blank" rel="noreferrer">{m.pageName || m.url}</a>
                                        </div>
                                    )}
                                    {renderAttachments(m)}
                                    {calls.filter((c) => c.messageId === m.id).map((c) => (
                                        <CallCard key={c.id} call={c} onChanged={onCallChanged} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div ref={bottomRef} />
            </div>

            {dialog?.deleted ? (
                <div className="task-composer dlg-archived">Диалог в архиве</div>
            ) : isBeeline ? (
                <div className="task-composer dlg-archived">Звонки сохраняются автоматически</div>
            ) : (
                <div className="task-composer">
                    <Input.TextArea
                        autoSize={{ minRows: 2, maxRows: 8 }}
                        placeholder="Напишите ответ… Ctrl+Enter — отправить"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => {
                            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                                e.preventDefault();
                                e.stopPropagation();
                                onSend();
                            }
                        }}
                    />
                    {files.length > 0 && (
                        <div className="task-composer-files">
                            {files.map((f) => (
                                <Tag
                                    key={f.id}
                                    closable
                                    icon={f.isImage ? <FileImageOutlined /> : <PaperClipOutlined />}
                                    onClose={() => onRemoveFile(f)}
                                >
                                    {f.name} <span style={{ color: '#bfbfbf' }}>{fmtSize(f.size)}</span>
                                </Tag>
                            ))}
                        </div>
                    )}
                    <div className="task-composer-bar">
                        {(dialog?.channelVid ?? dialog?.vid) !== CHANNEL_MAX_WAPPI
                            && (dialog?.channelVid ?? dialog?.vid) !== CHANNEL_TG_WAPPI && (
                            <Upload showUploadList={false} beforeUpload={onUpload}>
                                <Tooltip title="Прикрепить файл">
                                    <Button type="text" size="small" icon={<PaperClipOutlined />} />
                                </Tooltip>
                            </Upload>
                        )}
                        <Dropdown
                            menu={{ items: patternItems, onClick: ({ key }) => applyPattern(Number(key)) }}
                            trigger={['click']}
                            onOpenChange={(open) => { if (open && !patterns.length) loadPatterns(); }}
                        >
                            <Tooltip title="Шаблоны">
                                <Button type="text" size="small" icon={<FormOutlined />} />
                            </Tooltip>
                        </Dropdown>
                        <Button size="small" type="text" onClick={onMarkUnread}>Отметить непрочитанным</Button>
                        <span>Ctrl+Enter — отправить</span>
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            loading={sending}
                            disabled={!text.trim() && !files.length}
                            onClick={onSend}
                        >
                            Отправить
                        </Button>
                    </div>
                </div>
            )}
            <ResizableDrawer
                open={rawModal.open}
                storageKey="rawJsonDrawerWidth"
                defaultWidth={600}
                title="Сырой JSON вебхука"
                onClose={() => setRawModal({ open: false, text: '' })}
                destroyOnClose
            >
                <div style={{ marginBottom: 8 }}>
                    <Button
                        icon={<CopyOutlined />}
                        onClick={() => {
                            navigator.clipboard?.writeText(rawModal.text);
                            message.success('Скопировано');
                        }}
                    >
                        Копировать
                    </Button>
                </div>
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>
                    {rawModal.text}
                </pre>
            </ResizableDrawer>
            <Modal
                open={!!videoModal}
                title={videoModal?.name}
                footer={null}
                width={800}
                centered
                destroyOnClose
                onCancel={() => setVideoModal(null)}
            >
                {videoModal && (
                    <video controls autoPlay src={fileUrl(videoModal.id, true)} style={{ width: '100%', maxHeight: '70vh', background: '#000' }} />
                )}
            </Modal>
            <PdfModal file={pdfModal} onClose={() => setPdfModal(null)} />
        </div>
    );
};

export default DialogMessages;
